import type { ReactNode } from 'react';

/**
 * One card of the redesigned pages: a head with a title and an actions slot,
 * then a body.
 *
 * The successor to `Section` (bits.tsx), which stays for every card phase A
 * moved without redesigning. The difference is small and deliberate: the head
 * carries the card's **verdict** (a `Chip`, a count, "Refreshed a minute ago")
 * rather than a note paragraph under the title, because the redesign's rule is
 * one verdict per card and the sentence belongs in the body.
 *
 * `tone="danger"` is the danger zone — the one card on a page whose title is
 * red because every button in it is a door that does not open back.
 */
export function Panel({
  title,
  actions,
  tone,
  className,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  tone?: 'danger' | undefined;
  className?: string | undefined;
  children?: ReactNode;
}): ReactNode {
  const classes = ['pane', tone === 'danger' ? 'pane--danger' : '', className ?? '']
    .filter((c) => c !== '')
    .join(' ');
  return (
    <section className={classes}>
      <header className="pane__head">
        <h3>{title}</h3>
        {actions === undefined ? null : <div className="pane__actions">{actions}</div>}
      </header>
      {children === undefined ? null : <div className="pane__body">{children}</div>}
    </section>
  );
}
